/**
 * Cria ou continua Appointment EVOLUTION e inicia o atendimento via verifyEncounter.
 * Sempre sem preço (booked). Não gera PaymentNotice.
 */
import { useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { endpoints, oids } from "@/configs/Settings";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { PostAppointment, PostSlot } from "@/services/fhir";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import { useFormatter } from "@/utils/useFormatter";
import { EVOLUTION_NOTE, EVOLUTION_TYPE_SERVICE } from "./constants";
import {
  fetchEvolutionPatient,
  type EvolutionPatient,
} from "./evolutionPatient";
import {
  fetchPairAppointments,
  findOpenEvolution,
} from "./evolutionAppointments";
import { resolveEvolutionReferences } from "./resolveEvolutionReferences";

export type { EvolutionPatient };

interface UsePatientEvolutionOptions {
  /** Schedules já carregados na agenda — evita GetSchedule extra. */
  schedules?: fhir5.Schedule[];
}

export function usePatientEvolution(options?: UsePatientEvolutionOptions) {
  const { user, access } = useSessionContext();
  const { organization } = useOrganizationContext();
  const { location } = useLocationContext();
  const { verifyEncounter } = useClinicalRegisterContext();
  const [loading, setLoading] = useState(false);
  const [hasOpenEvolution, setHasOpenEvolution] = useState(false);
  const professionalActor = {
    reference: `Practitioner/${oids.cpf}-${user.username}`,
    display: useFormatter.formatNameDisplay(user.name || ""),
  };

  const peekOpenEvolution = async (patientId: string) => {
    try {
      const bundle = await fetchPairAppointments({
        patientId,
        professionalRef: professionalActor.reference,
      });
      const open = findOpenEvolution(bundle);
      setHasOpenEvolution(Boolean(open?.id));
      return open;
    } catch (error) {
      console.error("peekOpenEvolution", error);
      setHasOpenEvolution(false);
      return undefined;
    }
  };

  const createEvolutionAppointment = async (
    patient: EvolutionPatient,
    pairBundle?: fhir5.Bundle,
  ) => {
    const refs = await resolveEvolutionReferences({
      patientId: patient.reference.replace("Patient/", ""),
      professionalActor,
      schedules: options?.schedules,
      locations: location,
      organizationId: access?.organizationId || organization?.id,
      pairBundle,
    });

    if (!refs?.scheduleId || !refs.locationActor?.reference) {
      toast.error("Não foi possível localizar a agenda/local do profissional");
      return null;
    }

    const start = dayjs();
    const end = start.add(refs.slotDurationMinutes, "minute");
    const createdSlot = await PostSlot({
      type: EVOLUTION_TYPE_SERVICE,
      scheduleId: refs.scheduleId,
      start: start.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      end: end.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
    });

    if (!createdSlot?.id) {
      throw new Error("Slot não criado");
    }

    // Tele/chat: nova sala Whereby — não reutiliza URL do último atendimento
    let virtualService:
      | Array<{ addressUrl: string; sessionKey: string }>
      | undefined;
    if (refs.needsWhereby) {
      try {
        const response = await PostWhereby({
          endDate: dayjs(createdSlot.end || end).format(
            "YYYY-MM-DDTHH:mm:ss-03:00",
          ),
          roomNamePrefix: `${endpoints?.AppName}-whereby`,
          roomNamePattern: "human-short",
        });
        if (response?.roomUrl) {
          virtualService = [
            {
              addressUrl: response.roomUrl,
              sessionKey: response.meetingId || "",
            },
          ];
        }
      } catch (wherebyError) {
        console.error("usePatientEvolution.whereby", wherebyError);
      }
    }

    const appointment = await PostAppointment({
      typeService: EVOLUTION_TYPE_SERVICE,
      cpf: patient.cpf,
      cpfResponsible: patient.responsibleCpf,
      appointmentIsFree: true,
      appointmentStatus: "booked",
      serviceCost: "",
      note: EVOLUTION_NOTE,
      schedule: refs.scheduleId,
      slotInfo: {
        id: createdSlot.id,
        start: createdSlot.start,
        end: createdSlot.end,
      },
      patientActor: {
        reference: patient.reference,
        display: patient.display,
      },
      locationActor: refs.locationActor,
      professionalActor: refs.professionalActor,
      ...(refs.healthCareServiceActor && {
        healthCareServiceActor: refs.healthCareServiceActor,
      }),
      ...(virtualService && { virtualService }),
    });

    if (!appointment?.id) {
      throw new Error("Appointment não criado");
    }

    return appointment as fhir5.Appointment;
  };

  /**
   * Se houver evolução booked, continua. Senão cria e inicia o Encounter.
   */
  const startOrContinueEvolution = async (patient: EvolutionPatient) => {
    if (!patient?.reference) {
      toast.error("Paciente inválido para evolução.");
      return false;
    }

    setLoading(true);
    try {
      const bundle = await fetchPairAppointments({
        patientId: patient.reference.replace("Patient/", ""),
        professionalRef: professionalActor.reference,
      });
      const open = findOpenEvolution(bundle);
      const appointment =
        open || (await createEvolutionAppointment(patient, bundle));

      if (!appointment?.id) return false;

      await verifyEncounter(appointment);
      return true;
    } catch (error) {
      console.error("startOrContinueEvolution", error);
      toast.error("Não foi possível iniciar a evolução.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const startOrContinueFromPatientId = async (patientId: string) => {
    const patient = await fetchEvolutionPatient(patientId);
    if (!patient) {
      toast.error("Não foi possível localizar o paciente.");
      return false;
    }
    return startOrContinueEvolution(patient);
  };

  return {
    loading,
    hasOpenEvolution,
    peekOpenEvolution,
    startOrContinueEvolution,
    startOrContinueFromPatientId,
    professionalDisplay: professionalActor.display,
  };
}
